import { View } from "react-native";
import { useTranslation } from "react-i18next";

import type { UiTheme } from "../theme";
import { Chip } from "../primitives/Chip";
import { ConfirmRemoveButton } from "../primitives/ConfirmRemoveButton";
import type { makeGridStyles } from "./gridStyles";

// PE-27 — the grid's structural handles: add a day or a week, drop the
// SELECTED one. Split out of IntensityGridView because it is a different job
// from rendering the grid (that file was carrying five concerns and closing on
// the 350-line soft cap), and because these four controls change together.
//
// ⭐ Both removals act on the CURSOR and name their target — "− W3", "− D2".
// The week − sat in the column header in the first cut and the visual bench
// threw it out twice over: it made the header 44 tall against a 20px pinned
// spacer, shearing every day label off its row by 4px each, and a bare row of
// dashes above the grid read as an empty row. With a cursor on screen "the
// week you point at" IS the selected week, so nothing was lost by moving it
// here — and the two removals became symmetric instead of one-per-column and
// one-per-selection.
export function GridStructureRow({
  theme,
  styles,
  selectedDay,
  selectedWeek,
  dayName,
  onAddDay,
  onAddWeek,
  onRemoveWeek,
  onRemoveDay,
  canRemoveWeek,
  canRemoveDay,
}: {
  theme: UiTheme;
  styles: ReturnType<typeof makeGridStyles>;
  selectedDay: number;
  selectedWeek: number;
  dayName: string;
  onAddDay?: () => void;
  onAddWeek?: () => void;
  onRemoveWeek?: (week: number) => void;
  onRemoveDay?: (dayIdx: number) => void;
  canRemoveWeek: boolean;
  canRemoveDay: boolean;
}) {
  const { t } = useTranslation();
  return (
    <View style={styles.gridStructureRow}>
      {onAddWeek ? (
        <Chip
          theme={theme}
          shape="box"
          selected={false}
          onPress={onAddWeek}
          label={`＋ ${t("composer.addWeek", { defaultValue: "Add week" })}`}
          accessibilityLabel={t("composer.addWeek", { defaultValue: "Add week" })}
        />
      ) : null}
      {onAddDay ? (
        <Chip
          theme={theme}
          shape="box"
          selected={false}
          onPress={onAddDay}
          label={`＋ ${t("composer.addDay", { defaultValue: "Add day" })}`}
          accessibilityLabel={t("composer.addDay", { defaultValue: "Add day" })}
        />
      ) : null}
      {canRemoveWeek ? (
        <ConfirmRemoveButton
          showRestIcon={false}
          removeLabel={`− W${selectedWeek + 1}`}
          confirmLabel={`✕ ${t("program.deleteConfirm", { defaultValue: "Confirm?" })}`}
          spokenRestLabel={`${t("composer.removeWeek", { defaultValue: "Remove week" })} — W${selectedWeek + 1}`}
          spokenConfirmLabel={t("composer.removeWeekConfirm", {
            defaultValue: "Confirm — drop this week and its sets",
          })}
          onRemove={() => onRemoveWeek!(selectedWeek)}
          style={styles.gridRemove}
          textStyle={styles.gridRemoveGlyph}
          armedStyle={styles.gridRemoveArmed}
          armedTextStyle={styles.gridRemoveGlyphArmed}
          hitSlop={8}
        />
      ) : null}
      {canRemoveDay ? (
        <ConfirmRemoveButton
          showRestIcon={false}
          removeLabel={`− ${dayName}`}
          // ⚠️ Interpolated, unlike the week − beside it, and that is not a
          // slip: en.json ships these keys with {{n}} inside, and re-declaring
          // a flat defaultValue for a key that already exists interpolated is
          // what the i18nDefaultValueDrift gate catches — it caught it.
          spokenRestLabel={t("composer.removeDay", {
            n: selectedDay + 1,
            defaultValue: "Remove day D{{n}}",
          })}
          spokenConfirmLabel={t("composer.removeDayConfirm", {
            n: selectedDay + 1,
            defaultValue: "Confirm — remove day D{{n}}?",
          })}
          confirmLabel={`✕ ${t("program.deleteConfirm", { defaultValue: "Confirm?" })}`}
          onRemove={() => onRemoveDay!(selectedDay)}
          style={styles.gridRemove}
          textStyle={styles.gridRemoveGlyph}
          armedStyle={styles.gridRemoveArmed}
          armedTextStyle={styles.gridRemoveGlyphArmed}
          hitSlop={8}
        />
      ) : null}
    </View>
  );
}
